import { Link } from "react-router-dom";
import { WalkthroughPanel } from "../components/Walkthrough";
import { HomePolicies } from "../components/PolicyAck";
import { HomeDocuments } from "../components/Documents";
import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, FolderOpen, Megaphone, GraduationCap, BadgeCheck, Eye, Check } from "lucide-react";
import { useAuth } from "../lib/auth";
import { api, errMsg, fmtDate, openFile } from "../lib/api";
import { useApi, PageHeader, Panel, StatusChip, Empty, Loading, Stat, ErrorNote, btn } from "../components/kit";

export function AssignmentRow({ a, to }) {
  const pct = a.sections_total ? Math.round((a.sections_done.length / a.sections_total) * 100) : 0;
  return (
    <Link to={to || `/training/${a.id}`} data-testid={`assignment-row-${a.id}`}
      className="group flex w-full min-w-0 flex-col gap-3 rounded-xl border bg-white p-4 hover:border-[color:var(--ink)] transition-colors sm:flex-row sm:items-center">
      <div className="flex-1 min-w-0">
        <p className="font-semibold truncate">{a.course_title} <span className="text-xs text-muted-foreground font-normal">v{a.version}</span></p>
        <p className="text-sm text-muted-foreground">
          {a.state === "completed" ? `Completed ${fmtDate(a.completed_at)}` : a.due_date ? `Due ${fmtDate(a.due_date)}` : "No due date set"}
        </p>
        {a.state !== "completed" && (
          <div className="mt-2 h-2 w-full max-w-xs rounded-full bg-muted" aria-label={`${pct}% of sections done`}>
            <div className="h-2 rounded-full bg-[color:var(--hops)]" style={{ width: `${pct}%` }} />
          </div>
        )}
      </div>
      <div className="flex items-center gap-3">
        <StatusChip status={a.state} />
        <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:translate-x-0.5 transition-transform" />
      </div>
    </Link>
  );
}

function HomeCertReviews() {
  const { data, reload } = useApi("/certificates/queue");
  const [busy, setBusy] = useState(null);
  const approve = async (c) => { setBusy(c.id); try { await api.post(`/certificates/${c.id}/review`, { decision: "accepted", reason: "" }); toast.success(`${c.user_name}'s ${c.type} certificate approved.`); reload(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); } };
  if (!data?.length) return null;
  return (
    <Panel title={<span className="flex items-center gap-2"><BadgeCheck className="h-5 w-5" /> Certificates to review ({data.length})</span>} testid="home-cert-reviews"
      action={<Link to="/reviews" className="text-sm font-semibold text-[color:var(--dcbc-red)]" data-testid="home-cert-reviews-all">Review now</Link>}>
      <ul className="grid gap-2">{data.slice(0, 5).map((c) => (
        <li key={c.id} className="flex flex-col gap-3 rounded-xl border bg-white p-3 sm:flex-row sm:items-center" data-testid={`home-cert-review-${c.id}`}>
          <span className="min-w-0 flex-1"><span className="font-semibold">{c.user_name}</span> <span className="text-muted-foreground">· {c.type}</span>
            <span className="block text-xs text-muted-foreground">Sent {fmtDate(c.submitted_at)}{c.expiry_date ? ` · expires ${fmtDate(c.expiry_date)}` : ""}</span></span>
          <span className="flex gap-2">
            {c.files?.[0] && <button className={btn.ghost} onClick={() => openFile(c.files[0].id).catch((e) => toast.error(errMsg(e)))} data-testid={`home-cert-view-${c.id}`}><Eye className="h-4 w-4" /> View</button>}
            <button className={btn.primary} disabled={busy === c.id} onClick={() => approve(c)} data-testid={`home-cert-approve-${c.id}`}><Check className="h-4 w-4" /> Approve</button>
          </span>
        </li>))}</ul>
      <p className="mt-2 text-xs text-muted-foreground">Need it fixed? Open <Link to="/reviews" className="font-semibold underline">Review Queues</Link> to send it back with a reason.</p>
      {data.length > 5 && <p className="mt-2 text-sm text-muted-foreground">and {data.length - 5} more</p>}
    </Panel>
  );
}

function Shortcut({ to, icon: Icon, title, text, tone, testid }) {
  return (
    <Link to={to} data-testid={testid}
      className={`flex min-h-[120px] flex-col justify-between rounded-2xl p-5 text-white active:scale-[0.99] transition-transform ${tone}`}>
      <Icon className="h-7 w-7" aria-hidden="true" />
      <div>
        <p className="font-display text-xl font-semibold">{title}</p>
        <p className="text-sm text-white/80">{text}</p>
      </div>
    </Link>
  );
}

export default function Home() {
  const { user } = useAuth();
  const { data, loading, error } = useApi("/dashboard/home");
  if (loading) return <Loading />;
  const open = (data?.assignments || []).filter((a) => a.state !== "completed");
  const done = (data?.assignments || []).filter((a) => a.state === "completed");
  return (
    <div className="space-y-8">
      <PageHeader eyebrow="Your DCBC Workplace" title={`Hi, ${user.name.split(" ")[0]}`} testid="home-header" />
      <ErrorNote error={error} />
      <div className="grid gap-4 sm:grid-cols-2">
        <Shortcut to="/records" icon={FolderOpen} title="My Records" text="Your training, certificates and documents" tone="bg-[color:var(--ink)]" testid="shortcut-records" />
        <Shortcut to="/concern" icon={Megaphone} title="Report a Concern" text="Hazard, near miss, incident or getting help" tone="bg-[color:var(--dcbc-red)]" testid="shortcut-concern" />
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="To do" value={open.filter((a) => a.state !== "overdue").length} testid="stat-todo" />
        <Stat label="Overdue" value={open.filter((a) => a.state === "overdue").length} tone="text-rose-700" testid="stat-overdue" />
        <Stat label="Completed" value={done.length} tone="text-emerald-700" testid="stat-completed" />
        <Stat label="Certificates awaiting" value={data.certs_awaiting.length} testid="stat-certs-awaiting" />
      </div>
      {(user.is_manager || user.is_superadmin) && <HomeCertReviews />}
      <WalkthroughPanel />
      <HomePolicies />
      <HomeDocuments />
      <Panel title="Required training" testid="home-training"
        action={<Link to="/training" className="text-sm font-semibold text-[color:var(--dcbc-red)]">All training</Link>}>
        {open.length === 0 ? <Empty testid="home-training-empty"><GraduationCap className="inline h-4 w-4 mr-1" />You're all caught up — no open training.</Empty>
          : <div className="grid gap-3">{open.map((a) => <AssignmentRow key={a.id} a={a} />)}</div>}
      </Panel>
      <div className="grid gap-6 lg:grid-cols-2">
        <Panel title="Certificates awaiting review" testid="home-certs">
          {data.certs_awaiting.length === 0 ? <Empty>No certificates waiting.</Empty> : (
            <ul className="divide-y">{data.certs_awaiting.map((c) => (
              <li key={c.id} className="flex items-center justify-between py-3"><span className="font-medium">{c.type}</span><StatusChip status={c.status} /></li>
            ))}</ul>)}
        </Panel>
        <Panel title="Upcoming expiries" testid="home-expiries">
          {data.expiries.length === 0 ? <Empty>No accepted certificates expiring in the next 60 days.</Empty> : (
            <ul className="divide-y">{data.expiries.map((c) => (
              <li key={c.id} className="flex items-center justify-between py-3">
                <span><span className="font-medium">{c.type}</span><span className="block text-xs text-muted-foreground">{c.expiry_date ? `Expires ${fmtDate(c.expiry_date)}` : "Expiry date not recorded"}</span></span>
                <StatusChip status={c.expiry_state} />
              </li>))}</ul>)}
        </Panel>
      </div>
    </div>
  );
}
